import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useContext, useMemo, useState } from 'react';

import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionMatrixContext, { type CellDraft, cellKey } from '@/Contexts/PermissionMatrix/Context';
import { combinedFormat } from '@/utils/helperFunctions';

interface CellScopeMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  action: string;
  entity: string;
  /** Whether the registry allows project scoping for this entity */
  projectScoped: boolean;
}

/**
 * Editor for one granted cell: which projects it reaches (when the entity
 * supports scoping) and when it expires. Company-wide + permanent is the
 * default a plain checkbox produces.
 */
const CellScopeMenu = ({ anchorEl, onClose, action, entity, projectScoped }: CellScopeMenuProps) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { draft, projectOptions, setCellScope, setCellExpiry } = useContext(PermissionMatrixContext);

  const cell: CellDraft | undefined = draft.get(cellKey(action, entity));
  const [scopeMode, setScopeMode] = useState<'all' | 'projects'>(
    cell?.projectIds === null || cell?.projectIds === undefined ? 'all' : 'projects',
  );

  const selectedProjects = useMemo(() => {
    if (!cell?.projectIds) return [];
    const ids = new Set(cell.projectIds);
    return projectOptions.filter((p) => ids.has(p.id));
  }, [cell, projectOptions]);

  if (!cell) return null;

  const handleScopeMode = (mode: 'all' | 'projects') => {
    setScopeMode(mode);
    if (mode === 'all') setCellScope(action, entity, null);
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{ paper: { sx: { p: 2, width: 360 } } }}
    >
      <Typography variant="subtitle2" fontWeight={800} gutterBottom>
        {action} · {entity.replace(/_/g, ' ')}
      </Typography>

      {projectScoped ? (
        <>
          <Typography variant="caption" color="text.secondary">
            Scope
          </Typography>
          <RadioGroup value={scopeMode} onChange={(_, value) => handleScopeMode(value as 'all' | 'projects')}>
            <FormControlLabel value="all" control={<Radio size="small" />} label="All projects" />
            <FormControlLabel value="projects" control={<Radio size="small" />} label="Specific projects" />
          </RadioGroup>

          {scopeMode === 'projects' && (
            <Autocomplete
              multiple
              size="small"
              options={projectOptions}
              value={selectedProjects}
              getOptionLabel={(option) => `${option.code} — ${option.title}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, values) =>
                setCellScope(
                  action,
                  entity,
                  values.map((v) => v.id),
                )
              }
              renderInput={(params) => <TextField {...params} placeholder="Select projects" />}
              sx={{ mt: 1 }}
            />
          )}

          <Divider sx={{ my: 2 }} />
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This module is not project-specific, so the permission always applies company-wide.
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary">
        Expires
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          format={combinedFormat(dateTimePreference)}
          value={cell.validTill ? dayjs(cell.validTill) : null}
          onChange={(value) => setCellExpiry(action, entity, value ? value.toISOString() : null)}
          slotProps={{
            textField: { size: 'small', fullWidth: true, placeholder: 'Never' },
            field: { clearable: true },
          }}
        />
      </LocalizationProvider>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button size="small" onClick={onClose}>
          Done
        </Button>
      </Box>
    </Popover>
  );
};

export default CellScopeMenu;
